import React, { useMemo, useRef, useState } from 'react';
import {
  Box,
  CircularProgress,
  IconButton,
  InputAdornment,
  List,
  ListItem,
  ListItemButton,
  Menu,
  MenuItem,
  OutlinedInput,
  Typography,
  useTheme,
} from '@mui/material';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import OptimusButton from '../../common/library/Button/Button';
import { TestIds } from '../../assets/testIds';
import { CoachConversationSummary } from '../../api/coachAPI';
import {
  buildCoachHistorySections,
  CoachHistoryGroup,
  CoachHistoryItem,
  CoachHistorySection,
} from '../../common/utils/coachHistory';
import { CoachHistoryStatus } from '../../common/customHooks/useAgentStream';
import { DeleteChatDialog } from './DeleteChatDialog';

// One rendered row of the list: a saved conversation, or the chat being started.
interface HistoryRow {
  conversationId: string;
  title: string;
  subtitle: string;
  testId: string;
  // Only saved conversations can be renamed or deleted - not the pending new chat.
  isSaved: boolean;
}

export interface ChatHistoryListProps {
  conversations: CoachConversationSummary[];
  historyStatus: CoachHistoryStatus;
  // Id of the chat currently on screen, highlighted in the list.
  activeConversationId: string;
  /**
   * True while the on-screen chat is a brand new one the gateway has not seen
   * yet. It is shown as a pending "New chat" entry at the top of the list, the
   * way it appears in the design.
   */
  isNewChat: boolean;
  onSelectConversation: (conversationId: string) => void;
  onRetry: () => void;
  // Rename and delete behind each saved row's ellipsis menu (SN-825). Both
  // reject when the change could not be saved.
  onRenameConversation: (conversationId: string, title: string) => Promise<void>;
  onDeleteConversation: (conversationId: string) => Promise<void>;
  /**
   * Marks the chat on screen with "(current)". Only makes sense in the dialog,
   * where the list is not sitting beside the chat it is describing.
   */
  markCurrentChat?: boolean;
}

/**
 * The grouped list of past conversations shared by the Chat History panel
 * (desktop) and the Chat History dialog (mobile), along with its loading, error
 * and empty states.
 *
 * Each saved row carries an ellipsis menu to rename the chat in place or delete
 * it after a confirmation (SN-825). The design's "Delete all" footer is not part
 * of that ticket and is still left out.
 */
export function ChatHistoryList({
  conversations,
  historyStatus,
  activeConversationId,
  isNewChat,
  onSelectConversation,
  onRetry,
  onRenameConversation,
  onDeleteConversation,
  markCurrentChat = false,
}: ChatHistoryListProps) {
  const theme = useTheme();
  const { t } = useTranslation();

  const sections = useMemo(() => buildCoachHistorySections(conversations), [conversations]);
  const isEmpty = historyStatus === 'ready' && sections.length === 0 && !isNewChat;

  // The row whose menu is open, with the button it hangs off.
  const [menu, setMenu] = useState<{ anchor: HTMLElement; row: HistoryRow } | null>(null);
  // The row being renamed in place, and what has been typed so far.
  const [editing, setEditing] = useState<{ conversationId: string; draft: string } | null>(null);
  const [rowPendingDelete, setRowPendingDelete] = useState<HistoryRow | null>(null);
  const [hasRenameError, setHasRenameError] = useState(false);
  // What was picked from the menu, run once the menu has finished closing. The
  // menu traps focus until its exit transition ends, so a rename field focused
  // any earlier is pulled straight back out - and the blur ends the edit.
  const pendingMenuActionRef = useRef<(() => void) | null>(null);

  const chooseFromMenu = (action: () => void) => {
    pendingMenuActionRef.current = action;
    setMenu(null);
  };

  const runPendingMenuAction = () => {
    const action = pendingMenuActionRef.current;
    pendingMenuActionRef.current = null;
    action?.();
  };

  const startRename = (row: HistoryRow) => {
    setHasRenameError(false);
    setEditing({ conversationId: row.conversationId, draft: row.title });
  };

  const startDelete = (row: HistoryRow) => {
    setRowPendingDelete(row);
  };

  // Saving an unchanged or blank name is treated as backing out: there is nothing
  // to store, and an empty title would leave the row with no name at all.
  const commitRename = (row: HistoryRow) => {
    if (!editing || editing.conversationId !== row.conversationId) {
      return;
    }
    const title = editing.draft.trim();
    setEditing(null);
    if (!title || title === row.title) {
      return;
    }
    onRenameConversation(row.conversationId, title).catch(() => setHasRenameError(true));
  };

  const confirmDelete = async () => {
    if (!rowPendingDelete) {
      return;
    }
    await onDeleteConversation(rowPendingDelete.conversationId);
    setRowPendingDelete(null);
  };

  const rowMenuButton = (row: HistoryRow) => (
    <IconButton
      data-testid={TestIds.coach.history.menuButton}
      aria-label={`${t('coach.history.chatActions')}`}
      aria-haspopup="menu"
      aria-expanded={menu?.row.conversationId === row.conversationId}
      size="small"
      onClick={(event) => setMenu({ anchor: event.currentTarget, row })}
      sx={{
        color: theme.palette.text.secondary,
        // The design keeps the button's hover circle on while its menu is open.
        ...(menu?.row.conversationId === row.conversationId && {
          backgroundColor: theme.palette.action.hover,
        }),
      }}
    >
      <MoreVertIcon fontSize="small" />
    </IconButton>
  );

  const renderRenameField = (row: HistoryRow) => (
    <OutlinedInput
      autoFocus
      fullWidth
      size="small"
      value={editing?.draft ?? ''}
      onChange={(event) => setEditing({
        conversationId: row.conversationId,
        draft: event.target.value,
      })}
      onFocus={(event) => event.target.select()}
      onBlur={() => commitRename(row)}
      onKeyDown={(event) => {
        if (event.key === 'Enter') {
          event.preventDefault();
          commitRename(row);
        } else if (event.key === 'Escape') {
          event.preventDefault();
          event.stopPropagation();
          setEditing(null);
        }
      }}
      inputProps={{
        'data-testid': TestIds.coach.history.renameInput,
        'aria-label': `${t('coach.history.renameLabel')}`,
        maxLength: 80,
      }}
      endAdornment={(
        <InputAdornment position="end">
          <IconButton
            data-testid={TestIds.coach.history.renameSaveButton}
            aria-label={`${t('coach.history.saveRename')}`}
            size="small"
            edge="end"
            // Keeps focus in the field so the click saves once, here, rather than
            // through the blur that would otherwise fire first.
            onMouseDown={(event) => event.preventDefault()}
            onClick={() => commitRename(row)}
            sx={{ color: theme.palette.brand.primary }}
          >
            <CheckCircleIcon fontSize="small" />
          </IconButton>
        </InputAdornment>
      )}
      sx={{
        backgroundColor: theme.palette.common.white,
        '& .MuiOutlinedInput-input': {
          padding: theme.spacing(0.75, 1),
          fontSize: theme.typography.bodyMd?.fontSize,
        },
      }}
    />
  );

  const renderRow = (row: HistoryRow) => {
    const isActive = row.conversationId === activeConversationId;
    const isEditing = editing?.conversationId === row.conversationId;

    const rowContent = (
      <>
        {isEditing ? renderRenameField(row) : (
          <Typography
            variant="bodyMd"
            sx={{
              color: theme.palette.text.primary,
              // The design gives the chat on screen a slightly heavier title.
              fontWeight: isActive ? 500 : 400,
              width: '100%',
              // Long titles stay on one line so every row is the same height.
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {/* escapeValue: false because React escapes this text itself. Without it
                i18next HTML-encodes the interpolated title, so a chat named
                "Owner's fees" renders literally as "Owner&#39;s fees" (SN-988). */}
            {markCurrentChat && isActive ?
              t('coach.history.currentChatTitle', {
                title: row.title,
                interpolation: { escapeValue: false },
              }) :
              row.title}
          </Typography>
        )}
        <Typography variant="labelSm" sx={{ color: theme.palette.text.secondary }}>
          {row.subtitle}
        </Typography>
      </>
    );
    const rowSx = {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'flex-start',
      gap: theme.spacing(0.25),
      padding: `${theme.spacing(1.5)} ${theme.spacing(2)}`,
    } as const;

    return (
      <ListItem
        key={row.conversationId}
        disablePadding
        // The menu button lives outside the row's button: a button inside a button
        // is invalid markup, and `secondaryAction` also reserves the room for it
        // so a long title cannot run underneath. It is hidden while renaming,
        // where it would sit on top of the field's save button.
        secondaryAction={row.isSaved && !isEditing ? rowMenuButton(row) : undefined}
        // The rule separates one row from the next, so the last row does not get
        // one: the panel's own border closes the list off (SN-988 node 2013:14119).
        // `last-of-type` counts only the `li` rows, not the group headings
        // between them, so it lands on the last row of the whole list.
        sx={{
          '&:not(:last-of-type)': {
            borderBottom: `1px solid ${theme.palette.divider}`,
          },
        }}
      >
        {/* While renaming, the row is a plain box rather than a button: the field
          and its save button cannot sit inside a button. */}
        {isEditing ? (
          <Box
            data-testid={row.testId}
            sx={{
              ...rowSx,
              width: '100%',
              backgroundColor: isActive ? theme.palette.action.selected : undefined,
            }}
          >
            {rowContent}
          </Box>
        ) : (
          <ListItemButton
            data-testid={row.testId}
            selected={isActive}
            onClick={() => onSelectConversation(row.conversationId)}
            sx={{
              ...rowSx,
              '&.Mui-selected, &.Mui-selected:hover': {
                backgroundColor: theme.palette.action.selected,
              },
            }}
          >
            {rowContent}
          </ListItemButton>
        )}
      </ListItem>
    );
  };

  const subtitleFor = (item: CoachHistoryItem) => (item.datePrefixKey ?
    `${t(item.datePrefixKey)} - ${item.dateLabel}` :
    item.dateLabel);

  const rowsOf = (section: CoachHistorySection): HistoryRow[] => section.items.map((item) => ({
    conversationId: item.conversationId,
    // The gateway names conversations asynchronously, so a chat that has only
    // just started falls back to its numbered placeholder.
    title: item.title ?? t('coach.history.conversationTitle', { number: item.titleNumber }),
    subtitle: subtitleFor(item),
    testId: TestIds.coach.history.item,
    isSaved: true,
  }));

  // The chat being started sits at the top of TODAY until its first turn reaches
  // the gateway and it comes back in the list as a saved conversation.
  const pendingRow: HistoryRow = {
    conversationId: activeConversationId,
    title: t('coach.history.newChatItem'),
    subtitle: `${t('coach.history.relative.today')} - ${dayjs().format('MMM D')}`,
    testId: TestIds.coach.history.newChatItem,
    isSaved: false,
  };

  const groups: { group: CoachHistoryGroup; rows: HistoryRow[] }[] = sections
    .map((section) => ({ group: section.group, rows: rowsOf(section) }));

  // While the list is still arriving the panel is only the spinner: the pending
  // row would otherwise sit under a TODAY heading beside it, as though the list
  // had loaded and this were the one chat in it (SN-988 mockup #5).
  if (isNewChat && historyStatus !== 'loading') {
    if (groups[0]?.group === 'today') {
      groups[0] = { group: 'today', rows: [pendingRow, ...groups[0].rows] };
    } else {
      groups.unshift({ group: 'today', rows: [pendingRow] });
    }
  }

  return (
    <>
      {historyStatus === 'loading' && (
        <Box
          data-testid={TestIds.coach.history.loading}
          sx={{
            display: 'flex', justifyContent: 'center', padding: theme.spacing(2),
          }}
        >
          {/* The design draws this in the org's brand colour, not MUI's primary. */}
          <CircularProgress size={24} sx={{ color: theme.palette.brand.primary }} />
        </Box>
      )}

      {historyStatus === 'error' && (
        <Box sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'flex-start',
          gap: theme.spacing(1),
          padding: `0 ${theme.spacing(2)} ${theme.spacing(2)}`,
        }}>
          <Typography
            variant="labelSm"
            data-testid={TestIds.coach.history.error}
            sx={{ color: theme.palette.error.main }}
          >
            {t('coach.history.loadError')}
          </Typography>
          <OptimusButton
            data-testid={TestIds.coach.history.retryButton}
            customVariant="link"
            buttonText={`${t('coach.history.retry')}`}
            onClick={onRetry}
            fullWidth={false}
            size="s"
          />
        </Box>
      )}

      {hasRenameError && (
        <Typography
          variant="labelSm"
          role="alert"
          data-testid={TestIds.coach.history.actionError}
          sx={{
            display: 'block',
            padding: `0 ${theme.spacing(2)} ${theme.spacing(1)}`,
            color: theme.palette.error.main,
          }}
        >
          {t('coach.history.renameError')}
        </Typography>
      )}

      {isEmpty && (
        <Typography
          variant="labelSm"
          data-testid={TestIds.coach.history.empty}
          sx={{ padding: `0 ${theme.spacing(2)} ${theme.spacing(2)}`, color: theme.palette.grey[600] }}
        >
          {t('coach.history.empty')}
        </Typography>
      )}

      {/* No padding below the last row either - the design runs the list right up
          to the bottom of the panel. */}
      <List disablePadding>
        {groups.map((group) => (
          <React.Fragment key={group.group}>
            <Typography
              variant="labelSmProminent"
              data-testid={TestIds.coach.history.groupLabel}
              sx={{
                display: 'block',
                padding: `${theme.spacing(1.5)} ${theme.spacing(2)} ${theme.spacing(1)}`,
                color: theme.palette.text.secondary,
                letterSpacing: '0.4px',
                textTransform: 'uppercase',
              }}
            >
              {t(`coach.history.groups.${group.group}`)}
            </Typography>
            {group.rows.map(renderRow)}
          </React.Fragment>
        ))}
      </List>

      <Menu
        data-testid={TestIds.coach.history.menu}
        anchorEl={menu?.anchor}
        open={Boolean(menu)}
        onClose={() => setMenu(null)}
        // Closing a menu hands focus back to its button, which would pull it
        // out of the rename field just opened. The field and the delete dialog
        // take focus themselves.
        disableRestoreFocus
        TransitionProps={{ onExited: runPendingMenuAction }}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        MenuListProps={{ dense: true }}
        // The theme strips every Paper's shadow; a menu floating over the list
        // needs one to read as a separate surface (per the Figma).
        PaperProps={{
          sx: {
            borderRadius: theme.spacing(0.5),
            boxShadow: theme.shadows[8],
          },
        }}
      >
        <MenuItem
          data-testid={TestIds.coach.history.renameMenuItem}
          onClick={() => menu && chooseFromMenu(() => startRename(menu.row))}
          sx={theme.typography.bodyMd}
        >
          {t('coach.history.rename')}
        </MenuItem>
        <MenuItem
          data-testid={TestIds.coach.history.deleteMenuItem}
          onClick={() => menu && chooseFromMenu(() => startDelete(menu.row))}
          sx={{ ...theme.typography.bodyMd, color: theme.palette.error.main }}
        >
          {t('coach.history.delete')}
        </MenuItem>
      </Menu>

      <DeleteChatDialog
        open={Boolean(rowPendingDelete)}
        chatTitle={rowPendingDelete?.title ?? ''}
        onCancel={() => setRowPendingDelete(null)}
        onConfirm={confirmDelete}
      />
    </>
  );
}
